import React, { useState } from "react";
import {
  EyeIcon,
  PrinterIcon,
  XMarkIcon,
  CalendarDaysIcon,
  UsersIcon,
  BanknotesIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  CheckCircleIcon as CheckCircleSolidIcon,
  ClockIcon as ClockSolidIcon,
} from "@heroicons/react/24/solid";

const PayrollDetailDialog = ({ open, onClose, payroll, onViewSalarySlip }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpansion = (employeeId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(employeeId)) {
      newExpandedRows.delete(employeeId);
    } else {
      newExpandedRows.add(employeeId);
    }
    setExpandedRows(newExpandedRows);
  };
  if (!payroll) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "calculated":
        return "bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border-blue-200";
      case "approved":
        return "bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border-green-200";
      case "paid":
        return "bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border-purple-200";
      default:
        return "bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border-blue-200"; // Default to calculated
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center">
                <DocumentTextIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Payroll Details
                </h2>
                <p className="text-gray-600">{payroll.payrollId}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div
                className={`px-4 py-2 rounded-full text-sm font-bold shadow-sm border ${getStatusColor(payroll.status)}`}
              >
                <div className="flex items-center space-x-1">
                  {payroll.status === "approved" && (
                    <CheckCircleIcon className="h-4 w-4" />
                  )}
                  {payroll.status === "calculated" && (
                    <ClockIcon className="h-4 w-4" />
                  )}
                  <span className="capitalize">{payroll.status}</span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Payroll Summary Cards */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Payroll Summary
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <CalendarDaysIcon className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-semibold text-blue-700 uppercase tracking-wide">
                    Period
                  </span>
                </div>
                <p className="text-lg font-bold text-blue-900">
                  {payroll.formattedPeriod}
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <UsersIcon className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-sm font-semibold text-green-700 uppercase tracking-wide">
                    Total Employees
                  </span>
                </div>
                <p className="text-lg font-bold text-green-900">
                  {payroll.totalEmployees}
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <BanknotesIcon className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-sm font-semibold text-purple-700 uppercase tracking-wide">
                    Gross Pay
                  </span>
                </div>
                <p className="text-lg font-bold text-purple-900">
                  {formatCurrency(payroll.totalGrossPay)}
                </p>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-2xl p-6 border border-red-100 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                    <BanknotesIcon className="h-4 w-4 text-red-600" />
                  </div>
                  <span className="text-sm font-semibold text-red-700 uppercase tracking-wide">
                    Deductions
                  </span>
                </div>
                <p className="text-lg font-bold text-red-900">
                  {formatCurrency(payroll.totalDeductions)}
                </p>
              </div>

              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-6 border border-emerald-100 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <BanknotesIcon className="h-4 w-4 text-emerald-600" />
                  </div>
                  <span className="text-sm font-semibold text-emerald-700 uppercase tracking-wide">
                    Net Pay
                  </span>
                </div>
                <p className="text-lg font-bold text-emerald-900">
                  {formatCurrency(payroll.totalNetPay)}
                </p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 border border-orange-100 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <DocumentTextIcon className="h-4 w-4 text-orange-600" />
                  </div>
                  <span className="text-sm font-semibold text-orange-700 uppercase tracking-wide">
                    TDS
                  </span>
                </div>
                <p className="text-lg font-bold text-orange-900">
                  {formatCurrency(payroll.totalTds)}
                </p>
              </div>
            </div>
          </div>

          {/* Employee Details Table */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Employee Details
            </h3>
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Employee
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Department
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Present Days
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Gross Earnings
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Deductions
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Net Salary
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {payroll.employeePayrolls?.map((employee) => {
                      {
                        console.log(employee);
                      }
                      return (
                        <React.Fragment key={employee.employeeId?._id}>
                          <tr className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-colors duration-200">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <button
                                  onClick={() =>
                                    toggleRowExpansion(
                                      employee.employeeId?.employeeId
                                    )
                                  }
                                  className="mr-3 p-1 hover:bg-gray-100 rounded transition-colors duration-200"
                                >
                                  {expandedRows.has(
                                    employee.employeeId?.employeeId
                                  ) ? (
                                    <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                                  ) : (
                                    <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                                  )}
                                </button>
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center mr-3">
                                  <UserIcon className="h-4 w-4 text-blue-600" />
                                </div>
                                <div>
                                  <div className="text-sm font-bold text-gray-900">
                                    {employee.employeeName}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    ID: {employee.employeeId?.employeeId}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-6 h-6 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center mr-2">
                                  <BuildingOfficeIcon className="h-3 w-3 text-purple-600" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {employee.department}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {employee.designation}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-bold text-cyan-700">
                                {employee.presentDays}
                              </div>
                              <div className="text-xs text-gray-500">days</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-bold text-green-700">
                                {formatCurrency(employee.grossEarnings)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-bold text-red-700">
                                {formatCurrency(employee.totalDeductions)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-bold text-blue-700">
                                {formatCurrency(employee.netSalary)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div
                                className={`inline-flex px-3 py-1 rounded-full text-xs font-bold shadow-sm border ${getStatusColor(employee.status)}`}
                              >
                                <div className="flex items-center space-x-1">
                                  {employee.status === "approved" && (
                                    <CheckCircleIcon className="h-3 w-3" />
                                  )}
                                  {employee.status === "calculated" && (
                                    <ClockIcon className="h-3 w-3" />
                                  )}
                                  <span className="capitalize">
                                    {employee.status}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() =>
                                  onViewSalarySlip(
                                    payroll._id,
                                    employee.employeeId?._id
                                  )
                                }
                                className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                                title="View Salary Slip"
                              >
                                <PrinterIcon className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                          {expandedRows.has(
                            employee.employeeId?.employeeId
                          ) && (
                            <tr className="bg-gradient-to-r from-gray-50 to-slate-50">
                              <td colSpan="8" className="px-6 py-4">
                                <div className="space-y-4">
                                  {/* Employee Info Cards */}
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
                                          <UserIcon className="h-3 w-3 text-blue-600" />
                                        </div>
                                        <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                                          Employee ID
                                        </span>
                                      </div>
                                      <p className="text-sm font-bold text-blue-900">
                                        {employee.employeeId?.employeeId}
                                      </p>
                                    </div>

                                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center">
                                          <BriefcaseIcon className="h-3 w-3 text-green-600" />
                                        </div>
                                        <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">
                                          Designation
                                        </span>
                                      </div>
                                      <p className="text-sm font-bold text-green-900">
                                        {employee.designation}
                                      </p>
                                    </div>

                                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <div className="w-6 h-6 bg-orange-100 rounded-lg flex items-center justify-center">
                                          <BanknotesIcon className="h-3 w-3 text-orange-600" />
                                        </div>
                                        <span className="text-xs font-semibold text-orange-700 uppercase tracking-wide">
                                          TDS Amount
                                        </span>
                                      </div>
                                      <p className="text-sm font-bold text-orange-900">
                                        {formatCurrency(employee.tds)}
                                      </p>
                                    </div>

                                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center">
                                          <CalendarDaysIcon className="h-3 w-3 text-purple-600" />
                                        </div>
                                        <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">
                                          Joining Date
                                        </span>
                                      </div>
                                      <p className="text-sm font-bold text-purple-900">
                                        {employee.dateOfJoining
                                          ? new Date(
                                              employee.dateOfJoining
                                            ).toLocaleDateString("en-GB")
                                          : "N/A"}
                                      </p>
                                    </div>
                                  </div>

                                  {/* Deductions Breakdown */}
                                  <div className="bg-white rounded-xl border border-gray-200 p-4">
                                    <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center">
                                      <BanknotesIcon className="h-4 w-4 text-red-600 mr-2" />
                                      Deductions Breakdown
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                      {employee.pfEmployee > 0 && (
                                        <div className="flex justify-between items-center p-2 bg-red-50 rounded-lg border border-red-100">
                                          <span className="text-xs font-medium text-red-700">
                                            PF Employee
                                          </span>
                                          <span className="text-xs font-bold text-red-900">
                                            {formatCurrency(
                                              employee.pfEmployee
                                            )}
                                          </span>
                                        </div>
                                      )}
                                      {employee.esiEmployee > 0 && (
                                        <div className="flex justify-between items-center p-2 bg-orange-50 rounded-lg border border-orange-100">
                                          <span className="text-xs font-medium text-orange-700">
                                            ESI Employee
                                          </span>
                                          <span className="text-xs font-bold text-orange-900">
                                            {formatCurrency(
                                              employee.esiEmployee
                                            )}
                                          </span>
                                        </div>
                                      )}
                                      {employee.professionalTax > 0 && (
                                        <div className="flex justify-between items-center p-2 bg-yellow-50 rounded-lg border border-yellow-100">
                                          <span className="text-xs font-medium text-yellow-700">
                                            Professional Tax
                                          </span>
                                          <span className="text-xs font-bold text-yellow-900">
                                            {formatCurrency(
                                              employee.professionalTax
                                            )}
                                          </span>
                                        </div>
                                      )}
                                      {employee.tds > 0 && (
                                        <div className="flex justify-between items-center p-2 bg-blue-50 rounded-lg border border-blue-100">
                                          <span className="text-xs font-medium text-blue-700">
                                            TDS
                                          </span>
                                          <span className="text-xs font-bold text-blue-900">
                                            {formatCurrency(employee.tds)}
                                          </span>
                                        </div>
                                      )}
                                      {employee.loanDeduction > 0 && (
                                        <div className="flex justify-between items-center p-2 bg-purple-50 rounded-lg border border-purple-100">
                                          <span className="text-xs font-medium text-purple-700">
                                            Loan Deduction
                                          </span>
                                          <span className="text-xs font-bold text-purple-900">
                                            {formatCurrency(
                                              employee.loanDeduction
                                            )}
                                          </span>
                                        </div>
                                      )}
                                      {employee.otherDeductions > 0 && (
                                        <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg border border-gray-100">
                                          <span className="text-xs font-medium text-gray-700">
                                            Other Deductions
                                          </span>
                                          <span className="text-xs font-bold text-gray-900">
                                            {formatCurrency(
                                              employee.otherDeductions
                                            )}
                                          </span>
                                        </div>
                                      )}
                                    </div>

                                    {/* Loan Repayment Details */}
                                    {employee.loanDeductionDetails &&
                                      employee.loanDeductionDetails.length >
                                        0 && (
                                        <div className="mt-4">
                                          <h5 className="text-xs font-bold text-gray-800 mb-2 flex items-center">
                                            <DocumentTextIcon className="h-3 w-3 text-purple-600 mr-1" />
                                            Loan Repayment Details
                                          </h5>
                                          <div className="space-y-2">
                                            {employee.loanDeductionDetails.map(
                                              (detail, index) => (
                                                <div
                                                  key={index}
                                                  className="flex justify-between items-center p-2 bg-purple-50 rounded-lg border border-purple-100"
                                                >
                                                  <div className="flex flex-col">
                                                    <span className="text-xs font-medium text-purple-700">
                                                      Loan ID:{" "}
                                                      {detail.loanApplicationId}
                                                    </span>
                                                    <span className="text-xs text-purple-600">
                                                      Installment{" "}
                                                      {detail.installmentNumber}
                                                    </span>
                                                  </div>
                                                  <div className="text-right">
                                                    <span className="text-xs font-bold text-purple-900">
                                                      {formatCurrency(
                                                        detail.amount
                                                      )}
                                                    </span>
                                                    <div className="text-xs text-purple-600">
                                                      Due:{" "}
                                                      {new Date(
                                                        detail.dueDate
                                                      ).toLocaleDateString(
                                                        "en-GB"
                                                      )}
                                                    </div>
                                                    <div className="text-xs text-purple-600">
                                                      Remaining:{" "}
                                                      {formatCurrency(
                                                        detail.remainingBalance
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                              )
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gradient-to-r from-gray-500 to-slate-600 hover:from-gray-600 hover:to-slate-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayrollDetailDialog;
